import { Injectable } from '@angular/core';
import { IStandardizedImageSearchResults } from 'src/models/standardized-image-search-results';
import { ExternalImageSearchApis } from '../../../config/external-image-search-api.config';
import { IImageWrapper } from '../../../models/image-wrapper';
import { Licenses } from '../../../models/licenses';
import { HttpService } from '../../services/http.service';

export interface IGoogleImageSearchQuery {
  q: string;
  l?: string; // license
  page?: number;
  s: string; // source: DD is duckduckgo
}

/**
 * Parameter for a flickr query
 */
export interface IGoogleImageSearchQueryOptions {
  license?: Licenses;
  /**
   * Targeted page of the results
   */
  page?: number;
}

export interface IGoogleStep1Result {
  started: boolean;
  search_id: string;
}

export interface IGoogleStep2Result {
  status: number;
  search_id?: string;
}

export interface IGooglePublicSearchImageItem {
  url: string;
  thumbnail_url: string;
  width: number;
  height: number;
  thumbnail_width: number;
  thumbnail_height: number;
  title: string;
  author: string;
  author_url: string;
  score: number;
  license: string;
  license_version: string;
  license_url: string;
} 

export interface IGoogleImageSearchResult {
  count: number;
  next: string;
  previous: string;
  results: IGooglePublicSearchImageItem[];
}

@Injectable({
  providedIn: 'root'
})
/**
 * Abstracts communication with Duckduckgo API
 */
export class GoogleImageSearchService {
  private _license: Licenses;


  /**
   * Makes use of the HTTPService
   * @param _http The http service
   */
   constructor(
    private _http: HttpService
  ) { }

  /**
   * Query the flickr API
   * @param query The actual query
   * @param options Some further options
   */
  public async query(query: string, options?: IGoogleImageSearchQueryOptions): Promise<IGoogleImageSearchResult> {
    const _queryObject: IGoogleImageSearchQuery = {
      q: query,
      page: (options && options.page) ? options.page : 1,
      s: "GL"
    };
    if (options && options.license) {
      this._license = options.license;
      let _convertedLicenses: string = '';
      _convertedLicenses += this.licenseTypeToDuckduckgoParam(options.license);
      _convertedLicenses = _convertedLicenses.slice(0, _convertedLicenses.length);
      _queryObject.l = _convertedLicenses;
    }

    // step 1
    const url1 = ExternalImageSearchApis['duckduckgo'].url + 'start?' + this._http.convertObjectToQueryString(_queryObject);
    const result1: IGoogleStep1Result = await this._http.get(url1);

    // step 2
    const url2 = ExternalImageSearchApis['duckduckgo'].url + result1.search_id + '/status'
    let result2: IGoogleStep2Result = {
      status: 0
    }

    while (result2.status < 100.0) {
      result2 = await this._http.get(url2)
      await new Promise(f => setTimeout(f, 1000)); // sleep 1 second
    }

    // step 3
    const url3 = ExternalImageSearchApis['duckduckgo'].url + result1.search_id + '?' + this._http.convertObjectToQueryString(_queryObject);
    const result: IGoogleImageSearchResult = await this._http.get(url3);
    return result;
  }

    /**
   * Convert the results into the internally used format
   * @param imageItems
   */
    public convertToImageWrapper(imageItems: IGooglePublicSearchImageItem[]): IImageWrapper[] {
      const results: IImageWrapper[] = [];
      for (const imageItem of imageItems) {
        results.push({
          externalId: null,
          base64: null,
          license: this._license,
          licenseVersion: null,
          licenseUrl: null,
          description: null,
          title: imageItem.title,
          source: imageItem.thumbnail_url, //imageItem.url,
          thumbnailSource: imageItem.thumbnail_url,
          author: imageItem.author, // TODO add author url?
          dimension: {
            height: imageItem.height,
            width: imageItem.width,
            ratio: imageItem.width / imageItem.height
          }
        });
      }
      return results;
    }

      /**
   * Convert to standardized results
   * @param results The results of the query
   * @param currentPage The result's current page
   * @param licenses The result's licenses
   */
       public async convertToStandardizedApiResults(imageSearchResults: IGoogleImageSearchResult, currentPage: number, licenses?: Licenses[]): Promise<IStandardizedImageSearchResults> {
        return {
          pageCount: 1, // Number(imageSearchResults..photos.pages),
          resultCount: Number(imageSearchResults.count),
          pageCurrent: currentPage,
          pageSize: Number(imageSearchResults.count),
          license: licenses,
          targetApi: 'google',
          results: await this.convertToImageWrapper(imageSearchResults.results)
        };
      }

  /**
   * Convert a license by name and version to the proper enum
   * @param licenseString
   */
       public licenseStringToType(licenseString: string): Licenses {
        return Licenses.UNKNOWN
      }

  /**
   * Convert the license to an id such that flickr understand the license
   * @param license The license to be converted to an id
   */
    public licenseTypeToDuckduckgoParam(license: Licenses): string {
      return "CC"
  }
}